import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';

const ContactForm = ({ user, contactToEdit, onActionComplete, onCancel }) => {
  // State for all form fields
  const [contactType, setContactType] = useState('Vendor');
  const [vendorType, setVendorType] = useState('Manufacturer');
  const [productionFocus, setProductionFocus] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [address, setAddress] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [website, setWebsite] = useState('');
  
  // State for UI feedback
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (contactToEdit) {
      setIsEditMode(true);
      setContactType(contactToEdit.contactType || 'Vendor');
      setVendorType(contactToEdit.vendorType || 'Manufacturer');
      setProductionFocus(contactToEdit.productionFocus || '');
      setCompanyName(contactToEdit.companyName || '');
      setContactPersonName(contactToEdit.contactPersonName || '');
      setAddress(contactToEdit.address || '');
      setContactNumber(contactToEdit.contactNumber || '');
      setEmail(contactToEdit.email || '');
      setInstagram(contactToEdit.instagram || '');
      setFacebook(contactToEdit.facebook || '');
      setWebsite(contactToEdit.website || '');
    }
  }, [contactToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    if (!user) {
      setMessage('Error: You must be logged in.');
      setIsSubmitting(false);
      return;
    }

    const contactData = { 
      userId: user.uid,
      contactType, companyName, contactPersonName, address, contactNumber, email, 
      instagram, facebook, website,
      vendorType: contactType === 'Vendor' ? vendorType : '',
      productionFocus: contactType === 'Vendor' ? productionFocus : '',
      lastUpdatedAt: new Date(),
    };

    try {
      if (isEditMode) {
        setMessage('Updating contact...');
        const contactRef = doc(db, "contacts", contactToEdit.id);
        await updateDoc(contactRef, contactData);
        setMessage('Contact has been updated successfully!');
        setTimeout(() => {
          onActionComplete();
        }, 2000);
      } else {
        setMessage('Saving contact...');
        contactData.createdAt = new Date();
        await addDoc(collection(db, "contacts"), contactData);
        setMessage('Contact has been saved successfully!');
        clearForm();
      }
    } catch (error) {
      console.error("Database Error:", error);
      setMessage(`Error: ${error.message}`);
    }

    setIsSubmitting(false);
  };
  
  const clearForm = () => {
    setContactType('Vendor');
    setVendorType('Manufacturer');
    setProductionFocus('');
    setCompanyName('');
    setContactPersonName('');
    setAddress('');
    setContactNumber('');
    setEmail('');
    setInstagram('');
    setFacebook('');
    setWebsite('');
  };

  return (
    <div className="form-section">
      <h3>{isEditMode ? 'Edit Contact' : 'Add New Contact'}</h3>
      <form onSubmit={handleSubmit} className="contact-form-grid">
        <div className="form-field full-span">
            <label>Contact Type:</label>
            <select value={contactType} onChange={(e) => setContactType(e.target.value)} disabled={isSubmitting}>
                <option value="Vendor">Vendor</option>
                <option value="Agent">Agent</option>
            </select>
        </div>

        {contactType === 'Vendor' && (
            <>
            <div className="form-field">
                <label>Vendor Type:</label>
                <select value={vendorType} onChange={(e) => setVendorType(e.target.value)} disabled={isSubmitting}>
                    <option value="Manufacturer">Manufacturer</option>
                    <option value="Wholesaler">Wholesaler</option>
                    <option value="Retailer">Retailer</option>
                    <option value="Online">Online</option>
                    <option value="Individual">Individual</option>
                    <option value="Not Defined">Not Defined</option>
                </select>
            </div>
            <div className="form-field">
                <label>Production Focus:</label>
                <input type="text" value={productionFocus} onChange={(e) => setProductionFocus(e.target.value)} placeholder="e.g., Jewels, Bags" disabled={isSubmitting}/>
            </div>
            </>
        )}

        <div className="form-field"><label>{contactType === 'Vendor' ? 'Vendor Name' : 'Agent Company'}:</label><input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required disabled={isSubmitting}/></div>
        <div className="form-field"><label>Contact Person:</label><input type="text" value={contactPersonName} onChange={(e) => setContactPersonName(e.target.value)} disabled={isSubmitting}/></div>
        <div className="form-field full-span"><label>Address:</label><textarea value={address} onChange={(e) => setAddress(e.target.value)} disabled={isSubmitting} rows="3"></textarea></div>
        <div className="form-field"><label>Contact Number:</label><input type="text" value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} disabled={isSubmitting}/></div>
        <div className="form-field"><label>Email Address:</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isSubmitting}/></div>
        <div className="form-field"><label>Instagram (Optional):</label><input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@username" disabled={isSubmitting}/></div>
        <div className="form-field"><label>Facebook URL (Optional):</label><input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="facebook.com/username" disabled={isSubmitting}/></div>
        <div className="form-field full-span"><label>Website (Optional):</label><input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.domain.com" disabled={isSubmitting}/></div>
        
        <div className="form-actions full-span">
            <button type="submit" disabled={isSubmitting} className="submit-button">
            {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Contact' : 'Save Contact')}
            </button>
            {isEditMode ? (
                <button type="button" disabled={isSubmitting} className="cancel-button" onClick={onActionComplete}>
                    Cancel Edit
                </button>
            ) : (
                <button type="button" disabled={isSubmitting} className="cancel-button" onClick={onCancel}>
                    Cancel
                </button>
            )}
        </div>
        
        {message && (
            <div className="popup-message full-span">
            {message}
            </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
